import { ItemType, ItemResponse } from "../models/ItemTypes";
import { ApiResponse } from "../models/uomtypes";


export const UNAUTHORIZED_EVENT = "unauthorized_access";

const getAuthToken = () => localStorage.getItem("authToken") || "";
const API_URL = process.env.REACT_APP_API_URL || "https://api-agamhive.agamvanigam.com";

interface FetchParams {
  page?: number;
  limit?: number;
  search?: string;
  filter?: Record<string, string | number | boolean>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

const handleUnauthorized = () => {
  console.error("Unauthorized access. Dispatching event for navigation...");
  localStorage.removeItem("authToken");
  
  const unauthorizedEvent = new CustomEvent(UNAUTHORIZED_EVENT);
  window.dispatchEvent(unauthorizedEvent);
  return { error: "Unauthorized. Please log in." };
};


export const fetchItems = async (params: FetchParams = {}): Promise<ApiResponse<ItemResponse>> => {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const queryParams = new URLSearchParams({
      page: (params.page || 1).toString(),
      limit: (params.limit || 10).toString(),
      ...(params.search ? { search: params.search } : {}),
      ...(params.filter
        ? Object.fromEntries(Object.entries(params.filter).map(([key, value]) => [key, String(value)]))
        : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    });
    
    const response = await fetch(`${API_URL}/items?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      return handleUnauthorized();
    }

    const jsonResponse = await response.json();
    if (!response.ok) {
      return { error: jsonResponse.msg || "Failed to fetch items" };
    }

    return jsonResponse;
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
};


export const createItem = async (item: Omit<ItemType, 'status'>): Promise<ApiResponse<ItemType>> => {
    const token = getAuthToken();
    if (!token) return handleUnauthorized();

    try {
        const response = await fetch(`${API_URL}/items`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(item),
        });

        if (response.status === 401) {
          return handleUnauthorized();
        }

        const data = await response.json();
        return response.ok ? { data } : { error: data?.msg || "Failed to create item." };
    } catch (error) {
        return { error: "Network error. Please check your connection and try again." };
    }
};


export async function updateItem(id: string, item: ItemType): Promise<ApiResponse<ItemType>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();
  const requestBody = {
    hsnCode: item.hsnCode,
    internalPartNo: item.internalPartNo,
    externalPartNo: item.externalPartNo,
    partName: item.partName,
    model: item.model,
    category: item.category,
    uom: item.uom,
    packingQty: item.packingQty,
    stockPerDay: item.stockPerDay,
    safetyDays: item.safetyDays,
    minReqDay: item.minReqDay,
    maxReqDays: item.maxReqDays,
    productionItem: item.productionItem,
    sellingItem: item.sellingItem,
    purchaseItem: item.purchaseItem,
    selfLife: item.selfLife,
    expiryDays: item.expiryDays,
    scheduleNo: item.scheduleNo,
    status: item.status,
    updatedAt: new Date().toISOString(),
  };

  try {
    const response = await fetch(`${API_URL}/items/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 401) {
      return handleUnauthorized();
    }

    const data = await response.json();

    if (!response.ok) {
      return { error: data?.msg || "Failed to update item." };
    }

    return { data };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
}

export async function deleteItem(id: string): Promise<ApiResponse<null>> {
  const token = getAuthToken();
  if (!token) return handleUnauthorized();

  try {
    const response = await fetch(`${API_URL}/items/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      return handleUnauthorized();
    }

    return response.ok ? { data: null } : { error: "Failed to delete item." };
  } catch (error) {
    return { error: "Network error. Please try again." };
  }
}